import type { DragMoveEvent } from '@dnd-kit/react';
import { useDragDropMonitor } from '@dnd-kit/react';
import type React from 'react';
import { useMemo, useRef } from 'react';
import type { DropCell, TileData } from './board-types';
import type { DropGeometry } from './drag-geometry';
import { cellFromDragEvent } from './drag-geometry';
import type { TileDragData } from './tile-drag-overlay';

export interface RackDropMonitorProps {
  /** The rack's region id — drags from anywhere else are left alone. */
  region: string;
  /** The board's droppable id. */
  boardId: string;
  geometry: DropGeometry;
  /** Where the tile would land from `cell`, or `null` where it would not land at all. */
  land: (tile: TileData, cell: DropCell) => TileData | null;
  /** Called whenever that landing changes, and with `null` once the tile is off the board or down. */
  onLandingChange: (landing: TileData | null) => void;
}

const sameLanding = (a: TileData | null, b: TileData | null): boolean =>
  a === b || (a !== null && b !== null && a.id === b.id && a.row === b.row && a.col === b.col && a.dir === b.dir);

/**
 * Follows a tile dragged out of the rack across the board, reporting where it would land if let go —
 * the landing a drop there then makes, turned and all. Renders nothing; it has to sit inside the
 * `DragDropProvider` its drags run under.
 */
export const RackDropMonitor: React.FC<RackDropMonitorProps> = ({
  region,
  boardId,
  geometry,
  land,
  onLandingChange,
}) => {
  const lastRef = useRef<TileData | null>(null);

  const listeners = useMemo(() => {
    const report = (landing: TileData | null) => {
      if (sameLanding(lastRef.current, landing)) return;
      lastRef.current = landing;
      onLandingChange(landing);
    };
    return {
      onDragMove: (event: DragMoveEvent) => {
        const data = event.operation.source?.data as TileDragData | undefined;
        if (!data?.tile || data.region !== region || event.operation.target?.id !== boardId) return report(null);
        const cell = cellFromDragEvent(event, geometry);
        report(cell ? land(data.tile, cell) : null);
      },
      onDragEnd: () => report(null),
    };
  }, [region, boardId, geometry, land, onLandingChange]);
  useDragDropMonitor(listeners);

  return null;
};
